import { DragDropModule } from '@angular/cdk/drag-drop';
import { TextFieldModule } from '@angular/cdk/text-field';
import { CommonModule, DatePipe } from '@angular/common';
import { Component, inject, Inject, OnInit, signal, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, FormsModule, NgModel, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { siteDataInput } from '../../../interfaces/labDataModel';
import { GetSDCaptureBySiteIdAndDateResponse, SaveSDCaptureRequest, SDCEquipmentSelection, SDEquipmentCategory, SDEquipmentLookup, SDTriggerLookup } from '../../../interfaces/sdcmodel';
import { SiteDataService } from '../../../services/sitedata.service';
import { AddeditSitedatacaptureComponent } from '../../sitedatacapture/addedit-sitedatacapture/addedit-sitedatacapture.component';
import { cleanNum, formatDateOnly, numericInputLimit } from '../../shared/commonutils/helper';
import { AlertDialogComponent } from '../../shared/components/alert-dialog/alert-dialog.component';
import { IdName } from '../../../interfaces/pricematrixmodel';
import { forkJoin } from 'rxjs';
import { LookUpService } from '../../../services/lookupservice';
import { ScreenName, LoadScheduleControlFlags } from '../../../interfaces/securityPermissions';
import { RolePermissionsService } from '../../../services/role-permissions.service';
import { RoleService } from '../../../services/role.service';

@Component({
  selector: 'app-addedit-sitedowntime',
  imports: [
      CommonModule, FormsModule, ReactiveFormsModule, MatCardModule, MatFormFieldModule,
      MatInputModule, MatButtonModule, MatTabsModule, MatDialogModule,
      MatIconModule, MatSlideToggleModule, MatDividerModule, MatSnackBarModule,
      MatDatepickerModule, DragDropModule,
      MatSelectModule, TextFieldModule
    ],
  templateUrl: './addedit-sitedowntime.component.html',
  styleUrl: './addedit-sitedowntime.component.scss'
})
export class AddeditSitedowntimeComponent implements OnInit {

  permissions = inject(RolePermissionsService);
  rolesService= inject(RoleService);
  controlPermissions: LoadScheduleControlFlags = new LoadScheduleControlFlags();
  @ViewChild('dcctrl') dcctrl!:NgModel;
  private datePipe = inject(DatePipe);
  attemptedSave = signal<boolean>(false);
  displayDate = '';
  impactTypes: IdName[] = [];
  triggerTypes: SDTriggerLookup[] = [];
  reasonSum = 0;
  selectedTabIndex = signal(0);
  proformaFlag = signal<boolean>(false);  
  displayValue = signal<string>('');
  canShowSaveButton = signal<boolean>(false);
  previousDayGasProduction = signal<number | null>(null);
  equipmentCategories = signal<SDEquipmentCategory[]>([]);
  private pendingEditEquipmentData: GetSDCaptureBySiteIdAndDateResponse[] | null = null;
  equipmentGroups = signal<Record<string, SDEquipmentLookup[]>>({});
  filteredEquipmentGroups = signal<Record<string, SDEquipmentLookup[]>>({});
  selectedEquipment = signal<SDCEquipmentSelection[]>([]);
  private pendingTriggerLookups = new Set<number>();
  private triggerLookupVersion = new Map<number, number>();
  readonly sequenceOptions = Array.from({ length: 100 }, (_, i) => i + 1);
  getCategoryLabel(category: SDEquipmentCategory): string {
    return category.name.replace(/_/g, ' ');
  }
  get totalSelected(): number {
    return this.selectedEquipment().length;
  }
  duplicateSequenceError = signal<number | null>(null);
  equipmentLabelMap = new Map<number, string>();
  text: string = ''
  sdcForm = new FormGroup({    
    isOraDowntime: new FormControl<boolean>(false),
    oraDowntimeHours: new FormControl<number | null>({ value: null, disabled: true, },[Validators.max(24)]),

    adEquipment: new FormControl<number | null>({ value: null, disabled: false }, [Validators.max(100)]),
    oraEquipment: new FormControl<number | null>({ value: null, disabled: false }, [Validators.max(100)]),
    upgrader: new FormControl<number | null>({ value: null, disabled: false }, [Validators.max(100)]),
    digesterBiology: new FormControl<number | null>({ value: null, disabled: false }, [Validators.max(100)]),
    feedstockQuantity: new FormControl<number | null>({ value: null, disabled: false }, [Validators.max(100)]),
    feedstockQuality: new FormControl<number | null>({ value: null, disabled: false }, [Validators.max(100)]),
    digestateManagement: new FormControl<number | null>({ value: null, disabled: false }, [Validators.max(100)]),
    external: new FormControl<number | null>({ value: null, disabled: false }, [Validators.max(100)])
  }, { validators: [this.validateDowntimeSum] });

  constructor(
    public dialogRef: MatDialogRef<AddeditSitedatacaptureComponent>,
    @Inject(MAT_DIALOG_DATA) public data: siteDataInput,
    private dialog: MatDialog,
    private service: SiteDataService,
    private lookupService: LookUpService
  ) {
    console.log('Dialog data received:', data);
    if(data.isEdit){
      this.text = `(${data.id})`;
    }
    data.userName = data.userName.split('@')[0];
    this.displayDate = this.datePipe.transform(data.dateCaptured || new Date(), 'MM/dd/yyyy') || '';
    this.canShowSaveButton.set(data.canShowSaveButton);
  }

  ngOnInit() {
    this.loadEquipmentData();
    this.fetchSiteConfiguration();
    this.setupSumListener();
    this.setupDowntimeToggleListener();
  }

  onDateCapturedChange(date: Date | null): void {
    if (this.data.isEdit || !date) {
      return;
    }
  
    this.data.dateCaptured = date;
    this.fetchSiteConfiguration(true);
  }

  loadEquipmentData() {
    this.lookupService.getSDTriggerLookup().subscribe(triggerTypes => this.triggerTypes = triggerTypes);

    this.service.getSDEquipmentCategories().subscribe(categories => {
      this.equipmentCategories.set(categories);
      if (categories.length === 0) {
        this.equipmentGroups.set({});
        this.filteredEquipmentGroups.set({});
        return;
      }

      forkJoin(categories.map(category => this.service.getSDEquipmentLookup(category.id)))
        .subscribe(lookupGroups => {
          const grouped: Record<string, SDEquipmentLookup[]> = {};
          categories.forEach((category, index) => {
            const categoryKey = String(category.id);
            grouped[categoryKey] = lookupGroups[index].filter(item => Number(item.categoryId) === Number(category.id));
            grouped[categoryKey].forEach(item => this.equipmentLabelMap.set(item.id, item.name));
          });
          this.equipmentGroups.set(grouped);
          this.filteredEquipmentGroups.set(grouped);
          if (this.pendingEditEquipmentData !== null) {
            this.restoreEquipmentSelections(this.pendingEditEquipmentData);
            this.pendingEditEquipmentData = null;
          }
        });
    });

    this.service.getImpactTypes().subscribe((resp: IdName[]) => {
      this.impactTypes = resp;
    });
    let roles = this.rolesService.getRolesAsString();
    this.permissions.getRolePermissionsNew(roles, ScreenName.Sitedowntime, 0).subscribe({
      next: (perms: LoadScheduleControlFlags) => {
        console.log("downtime perms", perms);
        this.controlPermissions = perms;
      }
    })
  }
  
  fetchSiteConfiguration(clearExisting = false) {
    var ipDate = formatDateOnly(this.data.dateCaptured);
    if(clearExisting){
      this.service.getsdcDynTabs(this.data.siteId?.toString() || null, ipDate ?? '').subscribe(tabsInfo => {
        console.log("tabs info", tabsInfo);
        this.previousDayGasProduction.set(
          tabsInfo.length > 0 ? tabsInfo[0].previousDayGasProduction : null
        );
        this.previousDayGasProduction.set(tabsInfo.length > 0 ? tabsInfo[0].previousDayGasProduction : null);
      });
      return;
    }
    this.service.getsdcDynTabs(this.data.siteId?.toString() || null, ipDate ?? '').subscribe(tabsInfo => {
      console.log('Fetched site configuration:', tabsInfo);
      this.previousDayGasProduction.set(tabsInfo.length > 0 ? tabsInfo[0].previousDayGasProduction : null);
      if (this.data.isEdit) {
        this.fetchAndPatchData();
      }
    });
  }

  onEquipmentPanelToggle(opened: boolean, typeName: string): void {
    if (!opened) {
      this.filteredEquipmentGroups.update(current => ({
        ...current,
        [typeName]: [...(this.equipmentGroups()[typeName] || [])]
      }));
    }
  }

  onEquipmentSelectionChange(typeName: string, selectedIds: number[]): void {
    const current = this.selectedEquipment();

    const removedItems = current.filter(s => s.typeName === typeName && !selectedIds.includes(s.equipmentId));

    let kept = current.filter(s => s.typeName !== typeName || selectedIds.includes(s.equipmentId));    

    const newItems: SDCEquipmentSelection[] = selectedIds
      .filter(id => !kept.some(s => s.typeName === typeName && s.equipmentId === id))
      .map(id => ({
          id: 0,
          equipmentId: id,
          typeName,
          minutes:     null,
          comments: "",
          impactId: null,
          processId: this.getProcessIdForCategory(typeName),
          triggerId: null
      }));

    this.selectedEquipment.set([...kept, ...newItems]);
  }

  getSelectedIdsForType(typeName: string): number[] {
    return this.selectedEquipment()
      .filter(s => s.typeName === typeName)
      .map(s => s.equipmentId);
  }
    
  onMinutesInput(equipmentId: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    let raw = input.value.replace(/[^0-9]/g, '');
    
    if (raw.length > 4) raw = raw.substring(0, 4);

    input.value = raw;
    const num = raw === '' ? null : parseInt(raw, 10);
    this.selectedEquipment.update(items =>
      items.map(item => item.equipmentId === equipmentId ? { ...item, minutes: num } : item)
    );
    this.updateTriggerId(equipmentId);
  }

  setupSumListener() {
    const reasonFields = [
      'adEquipment', 'oraEquipment', 'upgrader', 'digesterBiology',
      'feedstockQuantity', 'feedstockQuality', 'digestateManagement', 'external'
    ];
    
    this.sdcForm.valueChanges.subscribe(val => {
      this.reasonSum = reasonFields.reduce((sum, field) => sum + (Number((val as any)[field]) || 0), 0);
    });
  }

  setupDowntimeToggleListener() {
    this.sdcForm.get('isOraDowntime')?.valueChanges.subscribe(isActive => {
      const hoursCtrl = this.sdcForm.get('oraDowntimeHours');
      if (isActive) {
        hoursCtrl?.enable();
        hoursCtrl?.setValidators([Validators.required, Validators.min(1), Validators.max(24)]);
      } else {
        hoursCtrl?.clearValidators();
        hoursCtrl?.disable();
        hoursCtrl?.setValue(null);
      }
      hoursCtrl?.updateValueAndValidity();
    });
  }

  validateDowntimeSum(control: AbstractControl): ValidationErrors | null {
    const isDowntime = control.get('isOraDowntime')?.value;
    if (!isDowntime) return null;

    const fields = ['adEquipment', 'oraEquipment', 'upgrader', 'digesterBiology', 'feedstockQuantity', 'feedstockQuality', 'digestateManagement', 'external'];
    const sum = fields.reduce((acc, f) => acc + (Number(control.get(f)?.value) || 0), 0);
    return sum === 100 ? null : { sumNot100: true };
  }

  filterequipment(event: Event, tyName: string): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    const allItems   = this.equipmentGroups()[tyName] || [];

    const filtered = !searchTerm
      ? [...allItems]
      : allItems.filter(item => item.name.toLowerCase().includes(searchTerm));
      
    this.filteredEquipmentGroups.update(current => ({ ...current, [tyName]: filtered}));
  }

  onKeydown(event: KeyboardEvent) {
    if(event.ctrlKey && event.metaKey){return;} 
    if(!/[0-9\/]/.test(event.key) && event.key.length === 1){
      event.preventDefault();
    }
  }

  dateformat(event: Event){
    const input = event.target as HTMLInputElement;
    let value = input.value;

    if(!value) return;
    const parts = value.split('/');
    if(parts.length !== 3) return;

    let [month, day, year] = parts.map(part => part.padStart(2, '0'));
    if(month.length === 1)  month = '0' + month;
    if(day.length === 1)  day = '0' + day;
    if(year.length === 2) year = '20' + year;
    input.value = `${month}/${day}/${year}`;
  }

  fetchAndPatchData(): void {
    const siteId = Number(this.data.siteId.split('-').pop());
    const dateCaptured = formatDateOnly(this.data.dateCaptured);
    if (!Number.isFinite(siteId) || !dateCaptured) {
      return;
    }

    this.service.getSDCaptureBySiteIdAndDate(siteId, dateCaptured).subscribe(equipmentData => {
      console.log("edit data", equipmentData)
      this.sdcForm.patchValue({ isOraDowntime: false, oraDowntimeHours: 0 });
      this.pendingEditEquipmentData = equipmentData;
      if (Object.keys(this.equipmentGroups()).length > 0) {
        this.restoreEquipmentSelections(equipmentData);
        this.pendingEditEquipmentData = null;
      }
    });
  }

  restoreEquipmentSelections(equipmentData: GetSDCaptureBySiteIdAndDateResponse[]): void {
    const restored: SDCEquipmentSelection[] = equipmentData.map(e => ({
      id: e.id,
      equipmentId: e.equipmentId,
      typeName: this.getTypeNameForEquipmentId(e.equipmentId),
      minutes: e.downtime,
      comments: e.comments,
      impactId: e.impactId != null && e.impactId > 0 ? e.impactId : null,
      processId: e.processId != null && e.processId > 0
        ? e.processId
        : this.getProcessIdForCategory(this.getTypeNameForEquipmentId(e.equipmentId)),
      triggerId: e.triggerId != null && e.triggerId > 0 ? e.triggerId : null
    }));
    this.selectedEquipment.set(restored);
    restored.forEach(item => {
      if (item.triggerId == null && item.minutes != null && item.impactId != null) {
        this.updateTriggerId(item.equipmentId);
      }
    });
  }
  private getTypeNameForEquipmentId(equipmentId: number): string {
    const groups = this.equipmentGroups();
    for (const typeName of Object.keys(groups)) {
      if (groups[typeName].some(e => e.id === equipmentId)) return typeName;
    }
    return '';
  }

  onSave() {
    this.attemptedSave.set(true);
    this.dcctrl.control?.markAsTouched();
    if(this.dcctrl.invalid) {
      return;
    }

    const selected = this.selectedEquipment();
    const total    = this.totalSelected;

    // ── Rule 1: at least one item must be selected ─────────────────────────
    if (selected.length === 0) {
      this.selectedTabIndex.set(0);
      this.dialog.open(AlertDialogComponent, {
        data: {
          title: 'Alert',
          message: 'Please select at least one item from any of the equipment dropdowns.'
        },
        disableClose: true,
        panelClass: 'delete-confirmation-dialog-nowrap'
      });
      return;
    }

    // ── Rule 2: all selected items must have a downtime mins ────────────────────
    const missingSequence = selected.filter(s => s.minutes === null || s.minutes === undefined);
    if (missingSequence.length > 0) {
      this.selectedTabIndex.set(0);
      this.dialog.open(AlertDialogComponent, {
        data: {
          title: 'Alert',
          message: `Please enter a downtime minutes for all selected equipment items.`
        },
        disableClose: true,
        panelClass: 'delete-confirmation-dialog-nowrap'
      });
      return;
    }

    const missingImpactType = selected.some(s => s.impactId == null || s.impactId <= 0);
    if (missingImpactType) {
      this.selectedTabIndex.set(0);
      this.dialog.open(AlertDialogComponent, {
        data: {
          title: 'Alert',
          message: 'Please select an impact type for all selected equipment items.'
        },
        disableClose: true,
        panelClass: 'delete-confirmation-dialog-nowrap'
      });
      return;
    }

    const missingTrigger = selected.find(s => s.triggerId == null || s.triggerId <= 0);
    if (missingTrigger) {
      if (!this.pendingTriggerLookups.has(missingTrigger.equipmentId)) {
        this.updateTriggerId(missingTrigger.equipmentId);
      }
      this.selectedTabIndex.set(0);
      this.dialog.open(AlertDialogComponent, {
        data: {
          title: 'Alert',
          message: 'Trigger information is still being determined. Please try saving again.'
        },
        disableClose: true,
        panelClass: 'delete-confirmation-dialog-nowrap'
      });
      return;
    }
    
    this.sdcForm.markAllAsTouched();
  
    if (this.sdcForm.invalid) {
      const hoursControl = this.sdcForm.get('oraDowntimeHours');
      if (hoursControl?.invalid) {
        this.selectedTabIndex.set(0);
        return;
      }
    }
    
    const raw = this.sdcForm.getRawValue();
    const nowISO = new Date().toISOString();
    let finalDateCaptured: string;
    if (this.data.dateCaptured) {
      const d = new Date(this.data.dateCaptured);
      finalDateCaptured = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString();
    } else {
      finalDateCaptured = nowISO;
    }

    const payload: SaveSDCaptureRequest = {
      siteId: parseInt(this.data.siteId.split('-')[1]),
      dateCaptured: finalDateCaptured,
      equipmentData: this.selectedEquipment().map(s => ({
        id:             s.id,
        equipmentId:    s.equipmentId,
        downtime: s.minutes!,
        comments: s.comments,
        impactId: s.impactId,
        processId: s.processId!,
        triggerId: s.triggerId!
      })),
      isUpdate: this.data.isEdit
    };

    console.log('Final Payload:', payload);
    this.service.saveSDTData(payload).subscribe(success => {
      if (success) {
        this.dialogRef.close(true);
      }
    });
  }

  onImpactChange(equipmentId: number, impactId: number | null): void {
    this.selectedEquipment.update(items =>
      items.map(item => item.equipmentId === equipmentId ? { ...item, impactId } : item)
    );
    this.updateTriggerId(equipmentId);
  }

  onTriggerChange(equipmentId: number, triggerId: number | null): void {
    // A manual choice takes precedence over any trigger lookup already in flight.
    this.triggerLookupVersion.set(equipmentId, (this.triggerLookupVersion.get(equipmentId) ?? 0) + 1);
    this.pendingTriggerLookups.delete(equipmentId);
    this.selectedEquipment.update(items => items.map(item =>
      item.equipmentId === equipmentId ? { ...item, triggerId } : item));
  }

  private updateTriggerId(equipmentId: number): void {
    const selection = this.selectedEquipment().find(item => item.equipmentId === equipmentId);
    if (!selection || selection.minutes == null || selection.impactId == null || selection.impactId <= 0) {
      this.triggerLookupVersion.set(equipmentId, (this.triggerLookupVersion.get(equipmentId) ?? 0) + 1);
      this.pendingTriggerLookups.delete(equipmentId);
      this.selectedEquipment.update(items => items.map(item =>
        item.equipmentId === equipmentId ? { ...item, triggerId: null } : item));
      return;
    }

    const requestedMinutes = selection.minutes;
    const requestedImpactId = selection.impactId;
    const version = (this.triggerLookupVersion.get(equipmentId) ?? 0) + 1;
    this.triggerLookupVersion.set(equipmentId, version);
    this.pendingTriggerLookups.add(equipmentId);
    this.selectedEquipment.update(items => items.map(item =>
      item.equipmentId === equipmentId ? { ...item, triggerId: null } : item));

    this.service.getSiteDowntimeTriggerId(requestedMinutes, requestedImpactId, equipmentId).subscribe({
      next: triggerId => {
        if (this.triggerLookupVersion.get(equipmentId) !== version) return;
        this.pendingTriggerLookups.delete(equipmentId);
        const current = this.selectedEquipment().find(item => item.equipmentId === equipmentId);
        if (current?.minutes !== requestedMinutes || current?.impactId !== requestedImpactId) return;
        this.selectedEquipment.update(items => items.map(item =>
          item.equipmentId === equipmentId ? { ...item, triggerId: Number(triggerId) || null } : item));
      },
      error: () => {
        if (this.triggerLookupVersion.get(equipmentId) === version) {
          this.pendingTriggerLookups.delete(equipmentId);
        }
      }
    });
  }

  onCommentsChange(equipmentId: number, event: Event): void {
    const comments = (event.target as HTMLTextAreaElement).value;
    this.selectedEquipment.update(items =>
      items.map(item => item.equipmentId === equipmentId ? { ...item, comments } : item)
    );
  }

  getEquipmentLabel(equipmentId: number): string {
    return this.equipmentLabelMap.get(equipmentId) ?? `Item {equipmentId}`;
  }

  getProcessNameForSelection(selection: SDCEquipmentSelection): string {
    return this.equipmentCategories().find(category => String(category.id) === selection.typeName)?.processName ?? '';
  }

  private getProcessIdForCategory(categoryId: string): number | null {
    return this.equipmentCategories().find(category => String(category.id) === categoryId)?.processId ?? null;
  }

  getTriggerName(selection: SDCEquipmentSelection): string {
    return this.triggerTypes.find(trigger => trigger.id === selection.triggerId)?.name ?? '';
  }

  onCancel() {
    this.dialogRef.close(false);
  }

  roundOff(event: Event, maxValue: number, maxDecimals: number, type: number, controlName: string): void {
    const input = event.target as HTMLInputElement;
    let val = input.value;
    
    val = numericInputLimit(event, maxValue, maxDecimals);
    
    this.displayValue.set(val);
    input.value = val;

    // 6. Parsing for the backing variables
    const isValueEmpty = val === '' || val === '.';
    if (isValueEmpty) {
      this.sdcForm.get(controlName)?.setValue(null, { emitEvent: false });
    } else if (val.endsWith('.')) {
      this.sdcForm.get(controlName)?.setErrors({ 'incompleteDecimal': true });
    } else {
      const parsedValue = parseFloat(val);
      this.sdcForm.get(controlName)?.setValue(parsedValue, { emitEvent: false });
    }
    requestAnimationFrame(() => {
      if (document.activeElement === input) {
        input.value = val;
      }
    });
  }

  onlyIntegers(event: KeyboardEvent) {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      event.preventDefault();
    }
  }

}
